import { useState } from "react";
import { X, ArrowsClockwise, CheckCircle, Warning, XCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const TONE = {
  pass: ["var(--c-green)", CheckCircle, "Compliant"],
  warn: ["var(--c-amber)", Warning, "Advisory"],
  fail: ["#dc2626", XCircle, "Not compliant"],
};

const WHERE_LABEL = {
  regenerate: "Regenerate this sheet",
  design: "Open the Design Engine",
  model: "Open the Model Engine",
  template: "Organisation sheet template",
};

/** Every sheet in the set, what is wrong with it in plain English, and the action that fixes it. */
export default function ComplianceDialog({ projectId, sheets, notGenerated = [], onClose, onDone }) {
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(() => {
    const first = sheets.find((s) => s.status !== "pass");
    return first ? first.type : null;
  });

  const remediate = async (types, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(`/drawings/${projectId}/compliance/remediate`,
        types ? { types } : {});
      const bad = (data.results || []).filter((r) => !r.ok);
      toast[bad.length ? "warning" : "success"](
        `${data.remediated} sheet(s) regenerated from the Design Model` +
        (bad.length ? ` · ${bad.length} could not be: ${bad[0].error}` : ""));
      await onDone?.();
      if (!bad.length && !types) onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(null); }
  };

  const nonCompliant = sheets.filter((s) => s.status !== "pass");

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/55 p-6 overflow-auto"
      data-testid="compliance-dialog">
      <div className="w-full max-w-3xl bg-[var(--c-surface)] border border-[var(--c-border)] rounded-xl shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[var(--c-border)]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--c-muted)]">
              Sheet compliance · {sheets.length} identified for this variant
            </div>
            <h2 className="text-lg text-[var(--c-text)] mt-1">
              {sheets.length - nonCompliant.length}/{sheets.length} sheets compliant
            </h2>
            <p className="text-[11px] text-[var(--c-muted)] mt-1 max-w-xl leading-snug">
              A sheet is compliant when it is projected from the Design Model the 3D viewer
              renders, carries the views and schedules its type calls for, is dimensioned off that
              geometry and states a true ISO 5455 scale. Fix the geometry in the Design Engine or
              the Model Engine; fix the sheet by regenerating it.
            </p>
          </div>
          <button data-testid="compliance-close" onClick={onClose}
            className="text-[var(--c-muted)] hover:text-[var(--c-text)]"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 flex items-center justify-between gap-3 bg-[var(--c-bg)]">
          <div className="text-[11px] text-[var(--c-muted)]">
            {nonCompliant.length
              ? `${nonCompliant.length} sheet(s) need regenerating. `
              : "Every sheet already passes. Regenerate the set after any change to the design. "}
            Approved sheets are reissued as the next revision — nothing approved is overwritten.
          </div>
          <button data-testid="compliance-fix-all" disabled={!!busy}
            onClick={() => remediate(nonCompliant.length ? null : sheets.map((s) => s.type), "all")}
            className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[var(--c-accent)] text-[var(--c-bg)] text-[11px] font-medium disabled:opacity-50">
            <ArrowsClockwise size={13} className={busy === "all" ? "animate-spin" : ""} />
            {busy === "all" ? "Working…"
              : nonCompliant.length ? "Make all compliant" : "Regenerate the whole set"}
          </button>
        </div>

        <div className="divide-y divide-[var(--c-border)] max-h-[58vh] overflow-auto">
          {sheets.map((s) => {
            const [color, Icon, label] = TONE[s.status] || TONE.warn;
            const bad = (s.checks || []).filter((c) => c.status !== "pass");
            const isOpen = open === s.type;
            return (
              <div key={s.type} data-testid={`compliance-row-${s.type}`}>
                <button onClick={() => setOpen(isOpen ? null : s.type)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[var(--c-accent)]/5">
                  <Icon size={15} style={{ color }} />
                  <span className="flex-1 text-[12px] text-[var(--c-text)] truncate">{s.name}</span>
                  <span className="text-[10px] text-[var(--c-muted)] font-mono">
                    {s.scale || "NTS"} · Rev {s.revision ?? "0"}
                  </span>
                  <span className="font-mono text-[10px] px-1.5 py-[1px] rounded-sm border"
                    style={{ color, borderColor: color }}>{s.score ?? 0}%</span>
                  <span className="text-[10px] w-24 text-right" style={{ color }}>
                    {bad.length ? `${bad.length} finding${bad.length > 1 ? "s" : ""}` : label}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 bg-[var(--c-bg)]" data-testid={`compliance-findings-${s.type}`}>
                    {!bad.length && (
                      <div className="text-[11px] text-[var(--c-green)] py-2">
                        Every check passes — projected from the Design Model, dimensioned off it and
                        issued at a true scale.
                      </div>
                    )}
                    {bad.map((c) => (
                      <div key={c.id} className="py-2 border-b border-[var(--c-border)]/60 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[9.5px] text-[var(--c-muted)]">{c.id}</span>
                          <span className="text-[11.5px] text-[var(--c-text)]">{c.title}</span>
                          <span className="font-mono text-[9px] uppercase px-1 rounded-sm"
                            style={{ color: (TONE[c.status] || TONE.fail)[0] }}>{c.status}</span>
                        </div>
                        <div className="text-[11px] text-[var(--c-muted)] mt-0.5">Found: {c.detail}</div>
                        {c.why && <div className="text-[11px] text-[var(--c-text)]/80 mt-1">{c.why}</div>}
                        {c.how && (
                          <div className="text-[11px] text-[var(--c-muted)] mt-1">
                            <b className="text-[var(--c-text)]">What to do: </b>{c.how}
                            {c.where && c.where !== "regenerate" && (
                              <span className="inline-flex items-center gap-1 ml-1 text-[var(--c-accent)]">
                                <ArrowSquareOut size={10} />{WHERE_LABEL[c.where]}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-3">
                      <button data-testid={`compliance-fix-${s.type}`} disabled={!!busy}
                        onClick={() => remediate([s.type], s.type)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[var(--c-border)] text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-50">
                        <ArrowsClockwise size={12} className={busy === s.type ? "animate-spin" : ""} />
                        {busy === s.type ? "Regenerating…" : "Make this sheet compliant"}
                      </button>
                      <span className="text-[10px] text-[var(--c-muted)]">
                        Regenerates {s.drawing_no || s.name} from the Design Model and re-runs every check.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {notGenerated.map((m) => (
            <div key={m.type} className="flex items-center gap-3 px-5 py-2.5"
              data-testid={`compliance-missing-${m.type}`}>
              <Warning size={15} className="text-[var(--c-muted)]" />
              <span className="flex-1 text-[12px] text-[var(--c-muted)] truncate">{m.name}</span>
              <span className="text-[10px] text-[var(--c-muted)]">not generated yet</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
